/**
 * Private Voice Chat Domain - LiveKit Integration for Moderator-Player Communication
 * Manages private voice chat connection state where moderators can speak with individual players.
 * Starting and ending a chat are the moderation API's mutations
 * (useStartPrivateChat, useEndPrivateChat): this holds the window's state.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { DisconnectReason } from 'livekit-client';
import { roomLinkAfter, type RoomFailure, type RoomLink } from '../room-failure';
import { forgetAtSessionEnd } from '../../../lib/session-end';

// TYPES

/** Where the call's room stands, as any moderated room does (RoomLink): the status row reads it so. */
type PrivateVoiceChatConnectionState = RoomLink['connectionState'];

interface WindowPosition {
  x: number;
  y: number;
}

interface PrivateVoiceChatState {
  // Connection info from server
  serverUrl: string | null;
  moderatorToken: string | null;
  roomName: string | null;

  // Player info
  playerCustomId: string | null;
  playerName: string | null;

  // Match info
  matchId: string | null;
  worldName: string | null;
  cohortName: string | null;

  // State
  connectionState: PrivateVoiceChatConnectionState;
  /**
   * Why the call cannot go on (room-failure.ts): set with the 'error' state,
   * and null in every other. LiveKit's own message goes to the log, never the
   * window.
   */
  failure: RoomFailure | null;

  // UI state
  windowPosition: WindowPosition;

  // Player speaking state
  isPlayerSpeaking: boolean;
  isPlayerMutedLocally: boolean; // Local mute control

  // Actions
  setConnectionInfo: (
    serverUrl: string,
    token: string,
    roomName: string,
    playerCustomId: string,
    playerName: string,
    matchId: string,
    worldName: string,
    cohortName: string
  ) => void;
  // The call's room, as LiveKit tells of it (roomLinkAfter, room-failure.ts).
  /** It connected. */
  roomConnected: () => void;
  /**
   * It went. A call that was connected and ends by no hand of the
   * moderator's has dropped, said so; one that failed keeps its failure; any
   * other has simply ended.
   */
  roomDisconnected: (reason?: DisconnectReason) => void;
  /** It cannot go on, and why: the failure whose words the window shows. */
  roomFailed: (failure: RoomFailure) => void;
  setWindowPosition: (position: WindowPosition) => void;
  setPlayerSpeaking: (isSpeaking: boolean) => void;
  togglePlayerMute: () => void;
  disconnect: () => void;
}

// STORE

const initialState: Pick<
  PrivateVoiceChatState,
  'serverUrl' | 'moderatorToken' | 'roomName' | 'playerCustomId' | 'playerName' | 'matchId' | 'worldName' | 'cohortName' | 'connectionState' | 'failure' | 'windowPosition' | 'isPlayerSpeaking' | 'isPlayerMutedLocally'
> = {
  serverUrl: null,
  moderatorToken: null,
  roomName: null,
  playerCustomId: null,
  playerName: null,
  matchId: null,
  worldName: null,
  cohortName: null,
  connectionState: 'disconnected',
  failure: null,
  windowPosition: { x: 20, y: 20 },
  isPlayerSpeaking: false,
  isPlayerMutedLocally: false,
};

export const usePrivateVoiceChatStore = create<PrivateVoiceChatState>()(
  devtools(
    (set): PrivateVoiceChatState => ({
      ...initialState,

      setConnectionInfo: (serverUrl, token, roomName, playerCustomId, playerName, matchId, worldName, cohortName) => {
        // Not who the player is: a log is no place for a student's name.
        console.log('[PrivateVoiceChat] Received the call\'s connection info');
        set(
          {
            serverUrl,
            moderatorToken: token,
            roomName,
            playerCustomId,
            playerName,
            matchId,
            worldName,
            cohortName,
            connectionState: 'connecting',
            failure: null,
            isPlayerMutedLocally: false,
          },
          false,
          'setConnectionInfo'
        );
      },

      roomConnected: () => {
        console.log('[PrivateVoiceChat] Connected to the room');
        set((state) => roomLinkAfter(state, { type: 'connected' }), false, 'roomConnected');
      },

      // A failed call keeps what it failed with: the same state comes back, so nothing renders again.
      roomDisconnected: (reason) => {
        console.log('[PrivateVoiceChat] Disconnected from the room', reason);
        set((state) => roomLinkAfter(state, { type: 'disconnected', reason }), false, 'roomDisconnected');
      },

      roomFailed: (failure) => {
        set((state) => roomLinkAfter(state, { type: 'failed', failure }), false, 'roomFailed');
      },

      setWindowPosition: (position) => {
        set({ windowPosition: position }, false, 'setWindowPosition');
      },

      // Asked every 100ms while the player is in the call: the same answer is
      // the same state, so nothing reading it renders again.
      setPlayerSpeaking: (isSpeaking) => {
        set((state) => (state.isPlayerSpeaking === isSpeaking ? state : { isPlayerSpeaking: isSpeaking }), false, 'setPlayerSpeaking');
      },

      togglePlayerMute: () => {
        set((state) => ({ isPlayerMutedLocally: !state.isPlayerMutedLocally }), false, 'togglePlayerMute');
      },

      // As the store starts, but where the window was left: the next call opens there.
      disconnect: () => {
        console.log('[PrivateVoiceChat] Disconnecting...');
        set((state) => ({ ...initialState, windowPosition: state.windowPosition }), false, 'disconnect');
      },
    }),
    { name: 'PrivateVoiceChatStore' }
  )
);

// The last call — its player, match, world and team — is not the next sign-in's.
forgetAtSessionEnd(() => usePrivateVoiceChatStore.getState().disconnect());
